<template>
  <div class="br-confirm__form">
    <div class="br-confirm__field">
      <label class="br-confirm__field-label">{{ $t('lbl_EMPBE_BR_Confirm_RegisteredName') }}</label>
      <b-form-input
        :value="value.registeredCompanyName"
        class="br-confirm__input br-confirm__input--multilang"
        :placeholder="$t('lbl_EMPBE_BR_Confirm_RegisteredName_Placeholder')"
        dir="auto"
        autocomplete="off"
        @input="patch('registeredCompanyName', $event)"
        @blur="markTouched('registeredCompanyName')"
      />
      <p v-if="visibleFieldErrors.registeredCompanyName" class="br-confirm__field-error">
        {{ $t('lbl_EMPBE_BR_Confirm_Error_Required', { field: $t('lbl_EMPBE_BR_Confirm_RegisteredName') }) }}
      </p>
    </div>

    <div class="br-confirm__field">
      <label class="br-confirm__field-label">{{ $t('lbl_EMPBE_BR_Confirm_RegistrationNo') }}</label>
      <p class="br-confirm__expiry-helper">{{ $t('lbl_EMPBE_BR_Confirm_RegistrationNo_Helper') }}</p>
      <b-form-input
        :value="value.registrationNo"
        class="br-confirm__input"
        :placeholder="$t('lbl_EMPBE_BR_Confirm_RegistrationNo_Placeholder')"
        @input="patch('registrationNo', $event)"
        @blur="markTouched('registrationNo')"
      />
      <p v-if="visibleFieldErrors.registrationNo" class="br-confirm__field-error">
        {{ $t('lbl_EMPBE_BR_Confirm_Error_Required', { field: $t('lbl_EMPBE_BR_Confirm_RegistrationNo') }) }}
      </p>
    </div>

    <div class="br-confirm__field br-confirm__field--expiry">
      <p class="br-confirm__field-label br-confirm__field-label--expiry">
        {{ $t('lbl_EMPBE_BR_Confirm_HasExpiry') }}
      </p>
      <p class="br-confirm__expiry-helper">{{ $t('lbl_EMPBE_BR_Confirm_HasExpiry_Helper') }}</p>

      <div class="animate-chk br-confirm__radio-list">
        <label class="br-confirm__radio-row d-block" for="br-expiry-no">
          <input
            id="br-expiry-no"
            :checked="value.hasExpirationDate === false"
            type="radio"
            class="radio_animated"
            name="br-has-expiry"
            :value="false"
            @change="patch('hasExpirationDate', false)"
          />
          {{ $t('lbl_EMPBE_BR_Confirm_Expiry_No') }}
        </label>
        <label class="br-confirm__radio-row d-block" for="br-expiry-yes">
          <input
            id="br-expiry-yes"
            :checked="value.hasExpirationDate === true"
            type="radio"
            class="radio_animated"
            name="br-has-expiry"
            :value="true"
            @change="patch('hasExpirationDate', true)"
          />
          {{ $t('lbl_EMPBE_BR_Confirm_Expiry_Yes') }}
        </label>
      </div>

      <div v-if="value.hasExpirationDate" class="br-confirm__date-row">
        <div
          class="br-confirm__outlined-field br-confirm__outlined-field--interactive"
          role="button"
          tabindex="0"
          @click="openDatePicker('startDateInput', $event)"
          @keydown.enter.prevent="openDatePicker('startDateInput', $event)"
          @keydown.space.prevent="openDatePicker('startDateInput', $event)"
        >
          <div class="br-confirm__outlined-label-row">
            <span class="br-confirm__outlined-label">{{ $t('lbl_EMPBE_BR_Confirm_StartDate') }}</span>
          </div>
          <div class="br-confirm__outlined-input-wrap">
            <span
              v-if="!value.startDate"
              class="br-confirm__date-placeholder"
              aria-hidden="true"
            >
              {{ $t('lbl_EMPBE_BR_Confirm_ExpiryDate_Placeholder') }}
            </span>
            <span
              v-else
              class="br-confirm__date-display"
              aria-hidden="true"
            >
              {{ value.startDate }}
            </span>
            <input
              ref="startDateInput"
              :value="value.startDate"
              type="date"
              class="br-confirm__outlined-input br-confirm__outlined-input--date"
              :class="{ 'br-confirm__outlined-input--empty': !value.startDate }"
              :aria-label="$t('lbl_EMPBE_BR_Confirm_StartDate')"
              @input="patch('startDate', $event.target.value)"
            />
            <img
              class="br-confirm__date-icon"
              :src="calendarIconUrl"
              width="24"
              height="24"
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>

        <div
          class="br-confirm__outlined-field br-confirm__outlined-field--interactive"
          role="button"
          tabindex="0"
          @click="openDatePicker('expiryDateInput', $event)"
          @keydown.enter.prevent="openDatePicker('expiryDateInput', $event)"
          @keydown.space.prevent="openDatePicker('expiryDateInput', $event)"
        >
          <div class="br-confirm__outlined-label-row">
            <span class="br-confirm__outlined-label">{{ $t('lbl_EMPBE_BR_Confirm_ExpiryDate') }}</span>
          </div>
          <div class="br-confirm__outlined-input-wrap">
            <span
              v-if="!value.expirationDate"
              class="br-confirm__date-placeholder"
              aria-hidden="true"
            >
              {{ $t('lbl_EMPBE_BR_Confirm_ExpiryDate_Placeholder') }}
            </span>
            <span
              v-else
              class="br-confirm__date-display"
              aria-hidden="true"
            >
              {{ value.expirationDate }}
            </span>
            <input
              ref="expiryDateInput"
              :value="value.expirationDate"
              type="date"
              class="br-confirm__outlined-input br-confirm__outlined-input--date"
              :class="{ 'br-confirm__outlined-input--empty': !value.expirationDate }"
              :min="expiryDateMin"
              :aria-label="$t('lbl_EMPBE_BR_Confirm_ExpiryDate')"
              @input="patch('expirationDate', $event.target.value)"
            />
            <img
              class="br-confirm__date-icon"
              :src="calendarIconUrl"
              width="24"
              height="24"
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
      <p v-if="visibleFieldErrors.datePeriod" class="br-confirm__field-error">
        {{ $t('lbl_EMPBE_BR_Confirm_Error_DatePeriod') }}
      </p>
    </div>

    <div class="br-confirm__display-block">
      <label class="br-confirm__field-label">
        {{ $t('lbl_EMPBE_BR_Confirm_DisplayName_Title') }}
      </label>
      <p
        class="br-confirm__display-desc"
        v-html="$t('lbl_EMPBE_BR_Confirm_DisplayName_Desc')"
      />
    </div>

    <div class="br-confirm__field br-confirm__field--outlined">
      <label class="br-confirm__outlined-field">
        <div class="br-confirm__outlined-label-row">
          <span class="br-confirm__outlined-label">
            {{ $t('lbl_EMPBE_BR_Confirm_DisplayName_Zh') }}
            <span class="br-confirm__required" aria-hidden="true">*</span>
          </span>
        </div>
        <div class="br-confirm__outlined-input-wrap">
          <input
            :value="value.displayNameZh"
            type="text"
            class="br-confirm__outlined-input"
            :placeholder="$t('lbl_EMPBE_BR_Confirm_DisplayName_Zh_Placeholder')"
            :disabled="submitting"
            required
            :aria-label="$t('lbl_EMPBE_BR_Confirm_DisplayName_Zh')"
            @input="patch('displayNameZh', $event.target.value)"
          />
        </div>
      </label>
      <p v-if="visibleFieldErrors.displayNameZh" class="br-confirm__field-error">
        {{ $t('lbl_EMPBE_BR_Confirm_Error_Required', { field: $t('lbl_EMPBE_BR_Confirm_DisplayName_Zh') }) }}
      </p>
    </div>

    <div class="br-confirm__field br-confirm__field--outlined">
      <label class="br-confirm__outlined-field">
        <div class="br-confirm__outlined-label-row">
          <span class="br-confirm__outlined-label">
            {{ $t('lbl_EMPBE_BR_Confirm_DisplayName_En') }}
            <span class="br-confirm__required" aria-hidden="true">*</span>
          </span>
        </div>
        <div class="br-confirm__outlined-input-wrap">
          <input
            :value="value.displayNameEn"
            type="text"
            class="br-confirm__outlined-input"
            :placeholder="$t('lbl_EMPBE_BR_Confirm_DisplayName_En_Placeholder')"
            :disabled="submitting"
            required
            :aria-label="$t('lbl_EMPBE_BR_Confirm_DisplayName_En')"
            @input="patch('displayNameEn', $event.target.value)"
          />
        </div>
      </label>
      <p v-if="visibleFieldErrors.displayNameEn" class="br-confirm__field-error">
        {{ $t('lbl_EMPBE_BR_Confirm_Error_Required', { field: $t('lbl_EMPBE_BR_Confirm_DisplayName_En') }) }}
      </p>
    </div>

    <div class="br-confirm__actions">
      <b-button
        type="button"
        variant="primary"
        class="br-confirm__submit-btn"
        :class="{ 'br-confirm__submit-btn--busy': submitting }"
        :disabled="submitting || !canSubmit"
        :aria-busy="submitting ? 'true' : 'false'"
        @click="$emit('submit')"
      >
        <span
          v-if="submitting"
          class="br-confirm__submit-spinner"
          aria-hidden="true"
        />
        {{ submitButtonLabel }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { BR_LICENSE_ASSETS } from '../../../constants/brLicenseAssets';
import {
  canSubmitBrConfirmForm,
  getBrConfirmFieldErrors,
} from '../utils/brConfirmSubmit';
import { getBrExpiryDateMinIso } from '../utils/brDocument';

export default {
  name: 'BrConfirmSubmitForm',
  props: {
    value: { type: Object, required: true },
    submitting: { type: Boolean, default: false },
    scanBlockerKeys: { type: Array, default: () => [] },
    /** 父级在每次扫描开始时递增，用于清空触碰态（避免空值立刻红字） */
    scanResetToken: { type: [Number, String], default: 0 },
  },
  data() {
    return {
      touched: {},
    };
  },
  computed: {
    calendarIconUrl() {
      return BR_LICENSE_ASSETS.calendarIcon;
    },
    /** 有效期至：最早今天（含当天） */
    expiryDateMin() {
      return getBrExpiryDateMinIso();
    },
    fieldErrors() {
      return getBrConfirmFieldErrors(this.value);
    },
    /**
     * 展示用错误：企业名 / 注册号仅在用户离开字段后才显示红字
     * （Macy：扫描提取不到值时只保留 placeholder，不立刻报 required）
     */
    visibleFieldErrors() {
      const all = this.fieldErrors;
      const visible = {};
      if (all.registeredCompanyName && this.touched.registeredCompanyName) {
        visible.registeredCompanyName = true;
      }
      if (all.registrationNo && this.touched.registrationNo) {
        visible.registrationNo = true;
      }
      if (all.datePeriod) {
        visible.datePeriod = true;
      }
      if (all.displayNameZh) {
        visible.displayNameZh = true;
      }
      if (all.displayNameEn) {
        visible.displayNameEn = true;
      }
      return visible;
    },
    canSubmit() {
      return canSubmitBrConfirmForm(this.value, this.scanBlockerKeys);
    },
    submitButtonLabel() {
      // 与留言 Save busy 一致：spinner + 原按钮文案，不改成「保存中…」
      return this.$t('lbl_EMPBE_BR_Confirm_Submit');
    },
  },
  watch: {
    scanResetToken() {
      this.touched = {};
    },
  },
  methods: {
    markTouched(key) {
      if (!key || this.touched[key]) {
        return;
      }
      this.touched = { ...this.touched, [key]: true };
    },
    patch(key, next) {
      this.$emit('input', { ...this.value, [key]: next });
    },
    openDatePicker(refName, event) {
      const input = this.$refs[refName];
      if (!input) return;
      if (event && event.target === input) return;
      if (typeof input.showPicker === 'function') {
        try {
          input.showPicker();
          return;
        } catch (error) {
          // ignore unsupported showPicker
        }
      }
      input.focus();
    },
  },
};
</script>
